import React, { useEffect, useState } from 'react';
import { Clapperboard, Download, X, ArrowLeft, KeyRound, Puzzle, RefreshCw } from 'lucide-react';
import Card from './ui/Card';
import { useAuthMeta } from './AuthProvider';
import { openApiSettings } from '../lib/openApiSettings';

// The Premiere Pro extension: the "try it" card signed-in users see above the
// tools, and the full install page at /premiere (the panel's Help button opens
// it). Version and release notes come from /api/v1/extension, the same source
// the panel's own update check reads.

interface ExtensionInfo {
  latest_version: string;
  min_version: string;
  download_url: string;
  file_name: string;
  releases: { version: string; date: string; notes: string[] }[];
}

const useExtensionInfo = () => {
  const [info, setInfo] = useState<ExtensionInfo | null>(null);
  useEffect(() => {
    let alive = true;
    fetch('/api/v1/extension')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => alive && setInfo(d))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return info;
};

const DownloadButton: React.FC<{ info: ExtensionInfo | null; compact?: boolean }> = ({ info, compact }) => (
  <a
    href={info?.download_url ?? '/api/v1/extension/download'}
    download
    className={`inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 font-semibold text-white shadow-md shadow-indigo-500/20 hover:opacity-90 transition-opacity ${compact ? 'px-4 py-2 text-sm' : 'px-5 py-2.5'}`}
  >
    <Download size={compact ? 16 : 18} />
    Download for Premiere Pro{info ? ` (v${info.latest_version})` : ''}
  </a>
);

const Step: React.FC<{ n: number; title: string; children: React.ReactNode }> = ({ n, title, children }) => (
  <li className="flex gap-3">
    <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-indigo-100 text-sm font-bold text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">{n}</span>
    <div>
      <p className="font-semibold text-slate-800 dark:text-slate-100">{title}</p>
      <div className="text-sm text-slate-600 dark:text-slate-300 space-y-1">{children}</div>
    </div>
  </li>
);

export const InstallSteps: React.FC<{ info: ExtensionInfo | null }> = ({ info }) => (
  <ol className="space-y-4">
    <Step n={1} title="Download the installer">
      <p>
        <DownloadButton info={info} compact /> <span className="ml-2 text-xs text-slate-500">{info?.file_name ?? 'TRB-Subtitle-Studio.ccx'}</span>
      </p>
      <p className="text-xs text-slate-500">Needs Premiere Pro 25.6 or newer, on Mac or Windows.</p>
    </Step>
    <Step n={2} title="Double-click the downloaded file">
      <p>Creative Cloud opens and warns that the plugin is not from the Adobe Marketplace. Choose <b>Install</b>.</p>
    </Step>
    <Step n={3} title="Open the panel in Premiere">
      <p>
        <b>Window → UXP Plugins → TRB Subtitle Studio</b>. Dock it next to the Timeline.
      </p>
    </Step>
    <Step n={4} title="Add your API key">
      <p>
        Make a key under{' '}
        <button type="button" onClick={openApiSettings} className="font-semibold text-indigo-600 dark:text-indigo-400 underline">
          API Settings → My API keys
        </button>{' '}
        and paste it into the panel's <b>Settings</b>. What you run is billed to your key, as on the site.
      </p>
    </Step>
  </ol>
);

/** The "try it" card above the tools; dismissed for good per user. */
export const PremierePromo: React.FC = () => {
  const { user } = useAuthMeta();
  const info = useExtensionInfo();
  const key = `premiere-promo-dismissed:${user?.email ?? 'anon'}`;
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  const [open, setOpen] = useState(false);
  if (hidden) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(key, '1');
    } catch {
      /* private mode */
    }
    setHidden(true);
  };

  return (
    <div className="glass-panel relative mb-8 rounded-2xl border border-indigo-200/60 p-5 dark:border-indigo-500/30" role="region" aria-label="Premiere Pro extension">
      <button type="button" onClick={dismiss} className="absolute right-3 top-3 rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800" aria-label="Don't show this again" title="Don't show this again">
        <X size={18} />
      </button>
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="flex items-start gap-3 flex-1 pr-6">
          <div className="rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 p-2.5 shadow-lg shadow-indigo-500/20">
            <Clapperboard className="h-6 w-6 text-white" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">New · try it</p>
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Subtitle Studio is now a Premiere Pro panel</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Hinglish subtitles straight from your sequence: review and fix the lines in the panel, then add them to the timeline. Transcription, English
              subtitles, vocal isolation, sound effects and voiceover are in there too.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <DownloadButton info={info} compact />
          <button type="button" onClick={() => setOpen((o) => !o)} className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">
            {open ? 'Hide steps' : 'How to install'}
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-5 border-t border-slate-200/60 pt-5 dark:border-slate-700/60">
          <InstallSteps info={info} />
          <p className="mt-4 text-xs text-slate-500">
            First release — if something doesn't work in your Premiere, tell the admin. More help on <a className="underline" href="/premiere">the extension page</a>.
          </p>
        </div>
      )}
    </div>
  );
};

/** /premiere — install, update and troubleshooting. */
export const PremiereExtensionPage: React.FC = () => {
  const info = useExtensionInfo();
  return (
    <div className="space-y-6">
      <a href="/" className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">
        <ArrowLeft size={16} /> Back to the tools
      </a>
      <Card title="Subtitle Studio for Premiere Pro" icon={<Clapperboard className="h-5 w-5" />}>
        <p className="mb-6 text-slate-600 dark:text-slate-300">
          A panel that sends your sequence's audio to Subtitle Studio and brings the result back into Premiere: Hinglish, transcription and English subtitles
          (reviewed in the panel before they reach the timeline), the voice isolated from music and noise, sound effects and voiceover.
        </p>
        <InstallSteps info={info} />
      </Card>

      <Card title="Using it" icon={<Puzzle className="h-5 w-5" />}>
        <ul className="list-disc space-y-2 pl-5 text-sm text-slate-600 dark:text-slate-300">
          <li>Open the sequence, pick <b>Whole sequence</b> or <b>In to Out</b>, and start a tool. The panel exports the sequence's mix as a small WAV and uploads it.</li>
          <li>
            For Hinglish you can add the episode's English SRT (sharper results) or a Hindi SRT (skips transcription). Only the lines inside the exported stretch
            are sent.
          </li>
          <li>The review screen lets you fix text and timings, split or merge lines, find and replace, shift everything, and jump the playhead to any line.</li>
          <li>
            <b>Add to timeline</b> saves the SRT in a <i>Subtitle Studio</i> folder beside the project, imports it into a <i>Subtitle Studio</i> bin and places it.
            If Premiere does not create the caption track itself, drag the SRT from the bin onto the timeline at 00:00 — its timings already match the sequence.
          </li>
          <li>Closing the panel doesn't lose work: a running job is picked up again and the review is kept as a draft.</li>
        </ul>
      </Card>

      <Card title="Updates" icon={<RefreshCw className="h-5 w-5" />}>
        <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">
          The panel checks on start-up. When a new version is out it shows <b>Update available</b>: download it and double-click it again. Most changes (options,
          defaults, limits, which tools are on) reach the panel without an update at all.
        </p>
        {info?.releases?.length ? (
          <div className="space-y-3">
            {info.releases.map((r) => (
              <div key={r.version}>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  v{r.version} <span className="font-normal text-slate-500">· {r.date}</span>
                </p>
                <ul className="list-disc pl-5 text-sm text-slate-600 dark:text-slate-300">
                  {r.notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : null}
      </Card>

      <Card title="If it doesn't install" icon={<KeyRound className="h-5 w-5" />}>
        <ul className="list-disc space-y-2 pl-5 text-sm text-slate-600 dark:text-slate-300">
          <li>Update Premiere Pro to 25.6 or newer, and make sure the Creative Cloud app is running and signed in.</li>
          <li>
            Install from the command line with Adobe's installer:
            <pre className="mt-1 overflow-x-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">
              {`Mac:  "/Library/Application Support/Adobe/Adobe Desktop Common/RemoteComponents/UPI/UnifiedPluginInstallerAgent/UnifiedPluginInstallerAgent.app/Contents/MacOS/UnifiedPluginInstallerAgent" --install ~/Downloads/TRB-Subtitle-Studio.ccx

Windows:  "C:\\Program Files\\Common Files\\Adobe\\Adobe Desktop Common\\RemoteComponents\\UPI\\UnifiedPluginInstallerAgent\\UnifiedPluginInstallerAgent.exe" /install %USERPROFILE%\\Downloads\\TRB-Subtitle-Studio.ccx`}
            </pre>
          </li>
          <li>Or load it with Adobe's UXP Developer Tool (Add Plugin → choose the manifest.json inside the unzipped .ccx).</li>
          <li>Premiere refuses the audio export? In the panel's Settings, choose a WAV export preset of your own (.epr).</li>
        </ul>
      </Card>
    </div>
  );
};
